import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, XCircle, Copy, Trash2, ChevronRight,
  Zap, RefreshCw, Pencil
} from "lucide-react";

const API = `${window.location.origin}/api`;

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "-"; }
}

export default function PriceListsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmActivate, setConfirmActivate] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/price-lists`, { headers });
      setLists(res.data);
    } catch { toast.error("Listeler yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLists(); }, []); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/price-lists`, { name: newName.trim() }, { headers });
      toast.success("Fiyat listesi oluşturuldu");
      setNewName("");
      setShowCreate(false);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Oluşturma başarısız");
    } finally { setCreating(false); }
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) return;
    setActionLoading(id);
    try {
      await axios.put(`${API}/price-lists/${id}`, { name: editName.trim() }, { headers });
      toast.success("İsim güncellendi");
      setEditId(null);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncelleme başarısız");
    } finally { setActionLoading(null); }
  };

  const handleActivate = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/price-lists/${id}/activate`, {}, { headers });
      toast.success("Fiyat listesi aktifleştirildi");
      setConfirmActivate(null);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktifleştirme başarısız");
    } finally { setActionLoading(null); }
  };

  const handleDuplicate = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/price-lists/${id}/duplicate`, {}, { headers });
      toast.success("Liste kopyalandı");
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kopyalama başarısız");
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (id) => {
    setActionLoading(id);
    try {
      await axios.delete(`${API}/price-lists/${id}`, { headers });
      toast.success("Liste silindi");
      setConfirmDelete(null);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    } finally { setActionLoading(null); }
  };

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Fiyat Listeleri
            </h1>
            <p className="text-sm text-gray-500 mt-1">{lists.length} liste mevcut</p>
          </div>
          <button
            data-testid="create-list-btn"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold uppercase tracking-wide rounded-md transition-all active:scale-95"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            <Plus className="w-4 h-4" /> Yeni Liste
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm animate-fade-in-up">
            <h3 className="text-sm font-bold uppercase text-gray-700 tracking-wider mb-3">Yeni Fiyat Listesi</h3>
            <form onSubmit={handleCreate} className="flex gap-3">
              <input
                data-testid="create-list-name-input"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                placeholder="Örn: Şubat 2025 KUBA Fiyat Listesi"
                autoFocus
              />
              <button
                data-testid="create-list-confirm-btn"
                type="submit"
                disabled={creating || !newName.trim()}
                className="px-4 py-2 bg-[#DC2626] text-white text-sm font-bold rounded-md hover:bg-[#B91C1C] disabled:opacity-50"
              >
                {creating ? "..." : "Oluştur"}
              </button>
              <button
                type="button"
                onClick={() => { setShowCreate(false); setNewName(""); }}
                className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : lists.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <List className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Henüz fiyat listesi yok. Yeni bir liste oluşturun.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="price-lists-table">
                <thead>
                  <tr className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b">
                    <th className="px-5 py-3 text-left">Liste Adı</th>
                    <th className="px-5 py-3 text-left">Durum</th>
                    <th className="px-5 py-3 text-left">Model Sayısı</th>
                    <th className="px-5 py-3 text-left">Oluşturulma</th>
                    <th className="px-5 py-3 text-left">Aktifleştirilme</th>
                    <th className="px-5 py-3 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {lists.map((pl) => (
                    <tr key={pl.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        {editId === pl.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              data-testid={`edit-list-name-input-${pl.id}`}
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="px-2 py-1 border border-[#DC2626] rounded text-sm focus:outline-none"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') handleEdit(pl.id); if (e.key === 'Escape') setEditId(null); }}
                            />
                            <button onClick={() => handleEdit(pl.id)} className="text-xs text-[#DC2626] font-bold">Kaydet</button>
                            <button onClick={() => setEditId(null)} className="text-xs text-gray-400">İptal</button>
                          </div>
                        ) : (
                          <span className="font-semibold text-gray-800">{pl.name}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {pl.status === "ACTIVE" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-sm">
                            <CheckCircle2 className="w-3 h-3" /> AKTİF
                          </span>
                        ) : (
                          <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-sm">
                            PASİF
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-gray-600 font-mono text-sm">{pl.item_count ?? 0}</td>
                      <td className="px-5 py-3.5 text-gray-500 text-xs">{formatDt(pl.created_at)}</td>
                      <td className="px-5 py-3.5 text-gray-500 text-xs">{formatDt(pl.activated_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {/* View Items */}
                          <button
                            data-testid={`view-items-btn-${pl.id}`}
                            onClick={() => navigate(`/fiyat-listeleri/${pl.id}`)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            title="Modelleri Görüntüle"
                          >
                            <ChevronRight className="w-3.5 h-3.5" /> Modeller
                          </button>

                          {/* Edit name */}
                          <button
                            data-testid={`edit-list-btn-${pl.id}`}
                            onClick={() => { setEditId(pl.id); setEditName(pl.name); }}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                            title="İsim Düzenle"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>

                          {/* Activate */}
                          {pl.status !== "ACTIVE" && (
                            <button
                              data-testid={`activate-btn-${pl.id}`}
                              onClick={() => setConfirmActivate(pl)}
                              disabled={actionLoading === pl.id}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                              title="Aktifleştir"
                            >
                              {actionLoading === pl.id ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Zap className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}

                          {/* Duplicate */}
                          <button
                            data-testid={`duplicate-btn-${pl.id}`}
                            onClick={() => handleDuplicate(pl.id)}
                            disabled={actionLoading === pl.id}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Kopyala"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete */}
                          {pl.status !== "ACTIVE" && (
                            <button
                              data-testid={`delete-list-btn-${pl.id}`}
                              onClick={() => setConfirmDelete(pl)}
                              disabled={actionLoading === pl.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Sil"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activate confirm modal */}
        {confirmActivate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Listeyi Aktifleştir?
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                <strong>{confirmActivate.name}</strong> aktif hale gelecek ve mevcut aktif liste pasife düşecek.
                Tüm API sorguları bu listeden yanıtlanacak.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="confirm-activate-btn"
                  onClick={() => handleActivate(confirmActivate.id)}
                  disabled={actionLoading === confirmActivate.id}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50"
                >
                  {actionLoading === confirmActivate.id ? "..." : "Aktifleştir"}
                </button>
                <button
                  onClick={() => setConfirmActivate(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm modal */}
        {confirmDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Listeyi Sil?
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                <strong>{confirmDelete.name}</strong> ve tüm modelleri silinecek. Bu işlem geri alınamaz.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="confirm-delete-list-btn"
                  onClick={() => handleDelete(confirmDelete.id)}
                  disabled={actionLoading === confirmDelete.id}
                  className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50"
                >
                  {actionLoading === confirmDelete.id ? "..." : "Sil"}
                </button>
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
